/**
 * Platform Tenants List Page
 * Shows all tenants with key stats and actions.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Building2,
  Users,
  MapPin,
  Plus,
  Search,
  ArrowUpRight,
  Circle,
  Pause,
  Play,
} from 'lucide-react';

const API = '/api';

export default function PlatformTenantsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTenant, setNewTenant] = useState({
    name: '', slug: '', plan: 'standard',
    admin_email: '', admin_password: '', admin_first_name: '', admin_last_name: '',
  });

  const fetchTenants = async () => {
    try {
      const { data } = await api.get('/platform/tenants');
      setTenants(data);
    } catch (err) {
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTenants(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/platform/tenants', newTenant);
      toast.success(`Tenant "${newTenant.name}" created`);
      setShowCreate(false);
      setNewTenant({ name: '', slug: '', plan: 'standard', admin_email: '', admin_password: '', admin_first_name: '', admin_last_name: '' });
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create tenant');
    } finally {
      setCreating(false);
    }
  };

  const handleSuspend = async (tenantId, name) => {
    if (!window.confirm(`Suspend tenant "${name}"? Their users will not be able to log in.`)) return;
    try {
      await api.post(`/platform/tenants/${tenantId}/suspend`);
      toast.success(`Tenant "${name}" suspended`);
      fetchTenants();
    } catch (err) {
      toast.error('Failed to suspend tenant');
    }
  };

  const handleActivate = async (tenantId, name) => {
    try {
      await api.post(`/platform/tenants/${tenantId}/activate`);
      toast.success(`Tenant "${name}" activated`);
      fetchTenants();
    } catch (err) {
      toast.error('Failed to activate tenant');
    }
  };

  const filtered = tenants.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.slug.toLowerCase().includes(search.toLowerCase())
  );

  const statusColor = (s) => {
    if (s === 'active') return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
    if (s === 'suspended') return 'bg-red-500/15 text-red-400 border-red-500/30';
    return 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30';
  };

  const planColor = (p) => {
    if (p === 'enterprise') return 'text-amber-400';
    if (p === 'professional') return 'text-blue-400';
    return 'text-zinc-400';
  };

  return (
    <div data-testid="platform-tenants-page" className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Tenants</h1>
          <p className="text-sm text-zinc-500 mt-0.5">{tenants.length} organisations on the platform</p>
        </div>

        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5"
              data-testid="create-tenant-btn"
            >
              <Plus className="w-4 h-4" /> New Tenant
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#18181b] border-zinc-800 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle>Provision New Tenant</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Create a new organisation and its first admin user.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300 text-xs">Company Name</Label>
                  <Input
                    required
                    value={newTenant.name}
                    onChange={e => setNewTenant({...newTenant, name: e.target.value})}
                    className="bg-zinc-900 border-zinc-700 text-white mt-1"
                    placeholder="Acme Security"
                    data-testid="new-tenant-name"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">Slug (URL)</Label>
                  <Input
                    required
                    pattern="[a-z0-9-]+"
                    value={newTenant.slug}
                    onChange={e => setNewTenant({...newTenant, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                    className="bg-zinc-900 border-zinc-700 text-white mt-1"
                    placeholder="acme"
                    data-testid="new-tenant-slug"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-300 text-xs">Plan</Label>
                <select
                  value={newTenant.plan}
                  onChange={e => setNewTenant({...newTenant, plan: e.target.value})}
                  className="w-full mt-1 bg-zinc-900 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm"
                  data-testid="new-tenant-plan"
                >
                  <option value="starter">Starter</option>
                  <option value="standard">Standard</option>
                  <option value="professional">Professional</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div className="border-t border-zinc-800 pt-3">
                <p className="text-xs text-zinc-500 mb-2">First Admin User</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-zinc-300 text-xs">First Name</Label>
                    <Input
                      required
                      value={newTenant.admin_first_name}
                      onChange={e => setNewTenant({...newTenant, admin_first_name: e.target.value})}
                      className="bg-zinc-900 border-zinc-700 text-white mt-1"
                      data-testid="new-tenant-admin-first"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300 text-xs">Last Name</Label>
                    <Input
                      required
                      value={newTenant.admin_last_name}
                      onChange={e => setNewTenant({...newTenant, admin_last_name: e.target.value})}
                      className="bg-zinc-900 border-zinc-700 text-white mt-1"
                      data-testid="new-tenant-admin-last"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <Label className="text-zinc-300 text-xs">Admin Email</Label>
                    <Input
                      required
                      type="email"
                      value={newTenant.admin_email}
                      onChange={e => setNewTenant({...newTenant, admin_email: e.target.value})}
                      className="bg-zinc-900 border-zinc-700 text-white mt-1"
                      data-testid="new-tenant-admin-email"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300 text-xs">Admin Password</Label>
                    <Input
                      required
                      type="password"
                      value={newTenant.admin_password}
                      onChange={e => setNewTenant({...newTenant, admin_password: e.target.value})}
                      className="bg-zinc-900 border-zinc-700 text-white mt-1"
                      data-testid="new-tenant-admin-password"
                    />
                  </div>
                </div>
              </div>
              <Button
                type="submit"
                disabled={creating}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="create-tenant-submit"
              >
                {creating ? 'Provisioning...' : 'Provision Tenant'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          placeholder="Search tenants..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9 bg-zinc-900/50 border-zinc-800 text-white"
          data-testid="tenant-search"
        />
      </div>

      {/* Tenant Cards */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500" />
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map(tenant => (
            <div
              key={tenant.id}
              className="bg-[#111114] border border-zinc-800/60 rounded-lg p-4 hover:border-amber-900/30 transition-colors group"
              data-testid={`tenant-card-${tenant.slug}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  {/* Icon */}
                  <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                    <Building2 className="w-5 h-5 text-amber-500" />
                  </div>

                  {/* Info */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-white truncate">{tenant.name}</span>
                      <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${statusColor(tenant.status)}`}>
                        {tenant.status}
                      </span>
                      <span className={`text-[10px] font-mono uppercase ${planColor(tenant.plan)}`}>
                        {tenant.plan}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-500 font-mono">
  {tenant.domain || `${tenant.slug}.rapidxtech.com.au`}
</p>
                  </div>

                  {/* Stats */}
                  <div className="hidden sm:flex items-center gap-6">
                    <div className="flex items-center gap-1.5 text-zinc-400">
                      <Users className="w-3.5 h-3.5" />
                      <span className="text-sm">{tenant.user_count}</span>
                      <span className="text-[10px] text-zinc-600">users</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-zinc-400">
                      <MapPin className="w-3.5 h-3.5" />
                      <span className="text-sm">{tenant.site_count}</span>
                      <span className="text-[10px] text-zinc-600">sites</span>
                    </div>
                    {tenant.created_at && (
                      <span className="text-[10px] text-zinc-600 font-mono">
                        {new Date(tenant.created_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 ml-4">
                  {tenant.status === 'active' ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSuspend(tenant.id, tenant.name)}
                      className="text-zinc-500 hover:text-red-400 hover:bg-red-500/10 text-xs h-7 px-2"
                      data-testid={`suspend-${tenant.slug}`}
                    >
                      <Pause className="w-3 h-3 mr-1" /> Suspend
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleActivate(tenant.id, tenant.name)}
                      className="text-zinc-500 hover:text-emerald-400 hover:bg-emerald-500/10 text-xs h-7 px-2"
                      data-testid={`activate-${tenant.slug}`}
                    >
                      <Play className="w-3 h-3 mr-1" /> Activate
                    </Button>
                  )}
                  <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/platform/tenants/${tenant.id}`)}
                className="text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 text-xs h-7 px-2"
                data-testid={`manage-${tenant.slug}`}
              >
                Manage
              </Button>

              <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      try {
                        const { data } = await api.post(`/platform/tenants/${tenant.id}/impersonate`, { access_mode: "read_only" });

                        localStorage.setItem("platform_return_access_token", localStorage.getItem("access_token") || "");
                        localStorage.setItem("platform_return_refresh_token", localStorage.getItem("refresh_token") || "");
                        localStorage.setItem("platform_return_user", localStorage.getItem("user") || "");

                        localStorage.setItem("access_token", data.access_token);
                        localStorage.removeItem("refresh_token");

                        localStorage.setItem("assist_mode", "true");
                        localStorage.setItem("assist_tenant_name", data.tenant_name || tenant.name);
                        localStorage.setItem("assist_tenant_slug", data.tenant_slug || tenant.slug);
                        localStorage.setItem("assist_started_at", String(Date.now()));
                        localStorage.setItem("assist_access_mode", "read_only");

                        if (data.user) {
                          localStorage.setItem("user", JSON.stringify(data.user));
                        }

                        toast.success(`Read-only assist mode active for ${tenant.name}`);

                        window.location.href = `/dashboard?tenant=${tenant.slug}&assist_mode=1`;
                      } catch (err) {
                        console.error(err);
                        toast.error(
                          err?.response?.data?.detail ||
                          "Failed to start read-only assist mode"
                        );
                      }
                    }}
                    className="text-zinc-500 hover:text-sky-400 hover:bg-sky-500/10 text-xs h-7 px-2"
                    data-testid={`readonly-${tenant.slug}`}
                  >
                    Read Only
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      try {
                        const { data } = await api.post(`/platform/tenants/${tenant.id}/impersonate`, { access_mode: "full" });

                        localStorage.setItem("platform_return_access_token", localStorage.getItem("access_token") || "");
                        localStorage.setItem("platform_return_refresh_token", localStorage.getItem("refresh_token") || "");
                        localStorage.setItem("platform_return_user", localStorage.getItem("user") || "");

                        localStorage.setItem("access_token", data.access_token);
                        localStorage.removeItem("refresh_token");

                        localStorage.setItem("assist_mode", "true");
                        localStorage.setItem("assist_tenant_name", data.tenant_name || tenant.name);
                        localStorage.setItem("assist_tenant_slug", data.tenant_slug || tenant.slug);
                        localStorage.setItem("assist_started_at", String(Date.now()));
                        localStorage.setItem("assist_access_mode", "full");

                        if (data.user) {
                          localStorage.setItem("user", JSON.stringify(data.user));
                        }

                        toast.success(`Full assist mode active for ${tenant.name}`);

                        window.location.href = `/dashboard?tenant=${tenant.slug}&assist_mode=1`;
                      } catch (err) {
                        console.error(err);
                        toast.error(
                          err?.response?.data?.detail ||
                          "Failed to start full assist mode"
                        );
                      }
                    }}
                    className="text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 text-xs h-7 px-2"
                    data-testid={`view-${tenant.slug}`}
                  >
                    Full Access <ArrowUpRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          ))}

          {filtered.length === 0 && !loading && (
            <div className="text-center py-12 text-zinc-500">
              {search ? 'No tenants match your search' : 'No tenants provisioned yet'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
